import type React from "react";

/**
 * Shared ticket-card silhouette: paper shape, punched notches and elevation.
 * The front (`TicketCard`) and the back (`TicketCardBack`) draw the exact same
 * outline so the flip keeps the physical card contour — shadow and holes stay
 * put while the content turns.
 */

/** Radius of the punched notches at the perforation. */
export const NOTCH_RADIUS_PX = 14;

/** Flex-grow ratios of the stub (top) and footer (bottom) sections. */
export const STUB_FLEX_GROW = 1.3;

export const FOOTER_FLEX_GROW = 1;

/**
 * Card outline + elevation as `drop-shadow` filters: unlike `box-shadow`
 * (rectangular), they follow the alpha contour, so the hairline edge and the
 * shadow wrap into the punched notches and make them read as real cut-outs.
 */
export const CARD_SHADOW_FILTER = "drop-shadow(0 0 0.6px rgba(0, 0, 0, 0.35)) drop-shadow(0 6px 10px rgba(0, 0, 0, 0.12))";

/**
 * Quarter-circle bite mask (`mask-image` + `-webkit-mask-image`): the corner
 * at `(x, y)` is made transparent so the card is really punched — whatever is
 * behind shows through, on any surface and on every stacked card.
 *
 * @param x Horizontal edge of the bite (`0%` left, `100%` right).
 * @param y Vertical edge of the bite (`0%` top, `100%` bottom).
 * @returns The inline style to apply.
 */
export const notchMask = ( x: "0%" | "100%", y: "0%" | "100%" ): React.CSSProperties => {
    const image = `radial-gradient(circle ${ NOTCH_RADIUS_PX }px at ${ x } ${ y }, transparent ${ NOTCH_RADIUS_PX - 1 }px, black ${ NOTCH_RADIUS_PX }px)`;

    return {
        "maskImage":       image,
        "WebkitMaskImage": image,
    };
};
